import { FoodCategory } from "../enums/FoodCatergory";
import { BookByDishCountUnit } from "../enums/BookByDishCountUnit";
import { BookByDishPlateUnit } from "../enums/BookByDishPlateUnit";

interface PlateDishSource {
    dishType?: string | null;
    plateSize?: number | null;
    plateUnit?: string | null;
}

// A booked/cart line's own unit snapshot. Lines are self-describing: a line stored in pieces
// keeps its piece count forever, even after its dish gains a plate configuration.
export interface PlateLineSource {
    count?: number | null;
    countUnit?: string | null;
    plateSize?: number | null;
    pieces?: number | null;
}

// Unit shown next to a legacy count — CURRY counts people, BREAD counts pieces.
export const dishCountUnit = (dishType?: string | null): string | null => {
    if (dishType === FoodCategory.CURRY) return "people";
    if (dishType === FoodCategory.BREAD) return "number";
    return null;
};

// True when a bread dish is sold by the plate. The backend additionally needs pricePerPlate and
// timePerPlate to price it, and rejects a PLATE write for a dish missing them.
export const isPlateDish = (dish?: PlateDishSource | null): boolean =>
    dish?.dishType === FoodCategory.BREAD && Number(dish?.plateSize) > 0 && !!dish?.plateUnit;

// The countUnit to send on a write for a newly picked dish: PLATE for a plate dish, else none.
export const countUnitForDish = (dish?: PlateDishSource | null): BookByDishCountUnit | undefined =>
    isPlateDish(dish) ? BookByDishCountUnit.PLATE : undefined;

export const isPlateLine = (line?: PlateLineSource | null): boolean =>
    line?.countUnit === BookByDishCountUnit.PLATE && Number(line?.plateSize) > 0;

// The noun for one plate. COUNT reads as "Qty": one unit is one piece.
export const plateUnitLabel = (plateUnit?: string | null, count: number = 1): string => {
    const plural = Number(count) !== 1;
    switch (plateUnit) {
        case BookByDishPlateUnit.SERVING:
            return plural ? "Servings" : "Serving";
        case BookByDishPlateUnit.COUNT:
            return "Qty";
        case BookByDishPlateUnit.PLATE:
        default:
            return plural ? "Plates" : "Plate";
    }
};

// Quantity text for a cart line / booked dish row, e.g. "2 Plates · 16 pcs". Keyed off the
// LINE's unit, never the dish's; only the unit noun comes from the dish.
export const lineQuantityText = (
    line: PlateLineSource | null | undefined,
    dish?: PlateDishSource | null,
): string => {
    const count = Number(line?.count ?? 0);
    if (!isPlateLine(line)) {
        const unit = dishCountUnit(dish?.dishType);
        return unit ? `${count} (${unit})` : `${line?.count ?? "-"}`;
    }
    const plateSize = Number(line?.plateSize);
    if (plateSize === 1) return `${count} Qty`;
    const pieces = Number(line?.pieces) || count * plateSize;
    return `${count} ${plateUnitLabel(dish?.plateUnit, count)} · ${pieces} pcs`;
};
